import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { Lock, ShieldCheck, ArrowRight, AlertCircle, Loader2, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/errors";

/**
 * ClaimAccount — end-point of the magic-link email.
 *
 * Reads `?token=` from URL. Two paths:
 *   • Welcome link (new user, no password) → prompt for a password + confirm,
 *     submit → email verified + password set + auto-signed-in.
 *   • Sign-in link (existing user, has password) → no form, just an
 *     "Open my dashboard" button that consumes the token and signs them in.
 *
 * We can't tell ahead of time which state the user is in without probing the
 * server, so we always render the password fields as OPTIONAL. If the user
 * already has a password, the backend rejects the new_password field and we
 * just sign them in.
 */
const ClaimAccount = () => {
    const [sp] = useSearchParams();
    const navigate = useNavigate();
    const { claimMagicLink } = useAuth();
    const token = sp.get("token") || "";
    const [showPassword, setShowPassword] = useState(false);
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [wantsPassword, setWantsPassword] = useState(true); // default: prompt for password
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    if (!token) {
        return (
            <div className="min-h-[calc(100vh-160px)] flex items-center justify-center px-6 py-16">
                <div className="w-full max-w-md text-center bg-white border border-red-200 rounded-2xl p-8" data-testid="claim-no-token">
                    <AlertCircle className="w-10 h-10 text-red-600 mx-auto mb-3" strokeWidth={1.5} />
                    <h1 className="font-heading text-2xl text-[#1C252E] font-medium">Missing link token</h1>
                    <p className="text-sm text-[#4A5568] mt-2">Open the link straight from your email, or request a new one from the sign-in page.</p>
                </div>
            </div>
        );
    }

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (wantsPassword) {
            if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
            if (!/[a-zA-Z]/.test(password) || !/\d/.test(password)) { setError("Password must include at least one letter and one digit."); return; }
            if (password !== confirm) { setError("Passwords don't match."); return; }
        }
        setSubmitting(true);
        try {
            const data = await claimMagicLink(token, wantsPassword ? password : undefined);
            if (data?.requires_2fa) {
                toast.message("Tap your security key to complete sign-in.");
                navigate("/login", { replace: true });
                return;
            }
            toast.success(wantsPassword ? "Password set — welcome to Reddog!" : "Signed in — welcome back!");
            navigate(["admin", "board"].includes(data?.role) ? "/admin" : "/account", { replace: true });
        } catch (err) {
            // If the user already has a password, the server returns 400 and
            // we quietly retry without new_password so they still get signed in.
            const msg = formatApiError(err) || "Couldn't sign you in";
            if (wantsPassword && /already has a password/i.test(msg)) {
                setWantsPassword(false);
                setError("This account already has a password. Click 'Sign me in' to continue.");
            } else {
                setError(msg);
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-[calc(100vh-160px)] flex items-center justify-center px-6 py-16" data-testid="claim-account-page">
            <Helmet><title>Claim your account · Reddog Foundation</title></Helmet>
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#E53935] mb-3">Verify + finish setup</p>
                    <h1 className="font-heading text-4xl text-[#1C252E] font-medium tracking-tight" data-testid="claim-heading">
                        Set your password
                    </h1>
                    <p className="text-sm text-[#4A5568] mt-3 leading-relaxed">
                        This will verify your email and let you sign in next time with a password.
                        Already have one? <button type="button" className="text-[#E53935] underline" onClick={() => setWantsPassword(false)} data-testid="claim-skip-password-btn">Skip and just sign me in</button>.
                    </p>
                </div>

                <form onSubmit={submit} className="bg-white border border-black/5 rounded-3xl p-8 space-y-4" data-testid="claim-form">
                    {wantsPassword ? (
                        <>
                            <div className="relative">
                                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A5568]" strokeWidth={1.5} />
                                <label htmlFor="claim-password" className="sr-only">New password</label>
                                <input
                                    id="claim-password"
                                    type={showPassword ? "text" : "password"}
                                    placeholder="New password (8+ chars, letter + digit)"
                                    autoComplete="new-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    minLength={8}
                                    data-testid="claim-password-input"
                                    className="w-full rounded-xl border border-black/10 pl-11 pr-12 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935] transition-all"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword((v) => !v)}
                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-full text-[#4A5568] hover:text-[#1C252E] hover:bg-[#F4F1EA]"
                                >
                                    {showPassword ? <EyeOff className="w-4 h-4" strokeWidth={1.5} /> : <Eye className="w-4 h-4" strokeWidth={1.5} />}
                                </button>
                            </div>
                            <div className="relative">
                                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A5568]" strokeWidth={1.5} />
                                <input
                                    type={showPassword ? "text" : "password"}
                                    placeholder="Confirm password"
                                    autoComplete="new-password"
                                    value={confirm}
                                    onChange={(e) => setConfirm(e.target.value)}
                                    required
                                    minLength={8}
                                    data-testid="claim-confirm-input"
                                    className="w-full rounded-xl border border-black/10 pl-11 pr-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935] transition-all"
                                />
                            </div>
                        </>
                    ) : (
                        <p className="text-sm text-[#4A5568] leading-relaxed" data-testid="claim-signin-only-hint">
                            You&apos;re signing in with a one-click magic link — no password needed this time.
                        </p>
                    )}

                    {error && (
                        <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="claim-error">
                            {error}
                        </p>
                    )}

                    <button
                        type="submit"
                        disabled={submitting}
                        data-testid="claim-submit-btn"
                        className="w-full inline-flex items-center justify-center gap-2 bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full py-3.5 font-medium transition-all disabled:opacity-60"
                    >
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                        {submitting ? "Signing you in…" : wantsPassword ? "Verify email & set password" : "Sign me in"}
                    </button>

                    <p className="text-[11px] text-[#4A5568] flex items-start gap-1.5 leading-relaxed">
                        <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" strokeWidth={2} />
                        This link is one-time-use and expires 60 minutes after we sent it.
                    </p>
                </form>
            </div>
        </div>
    );
};

export default ClaimAccount;
